import { BaseModel } from "./BaseModel";
import IAuthorizationData, {
  IPlayerConfig,
  IGameConfig,
} from "../server/data/IAuthorizationData";
import ISpinData from "../server/data/ISpinData";
import { IMoneyFieldConfig } from "../../src/views/AbstractMoneyField";
import ConfigManager from '../managers/ConfigManager';
import { ConfigurationTypes } from '../const/ConfigurationTypes';
import ConsoleUtil from "../utils/ConsoleUtil";
import ReelConfigurator from "../utils/ReelConfigurator";
import { version } from "../../../../package.json";
import Configs from "../../../src/vendorConfigs/Configs";

class GameModel extends BaseModel<IAuthorizationData> {
  public playerConfig: IPlayerConfig = null;
  public authToken: string = "";
  public casino: string = "";
  public operatorToken: string = "";
  public playerToken: string = "";
  public sessionID: string = "";
  public apiKey: string = "";
  public clock: boolean;
  public _gameConfig: any;
  public incompleteSpinsData: ISpinData = null;
  public currency: string;
  public coinsAmount: number;
  public lobbyURL: string = "";
  public lang: string = "en";
  public mobile: boolean;
  public android: boolean;
  public iOS: boolean;
  public isMac: boolean;
  public iOSMajorVersion: number;
  public isIpad: boolean;
  public symbolMap: Map<string, number>;
  public version: number = 0;
  public gameVersion: string = version;
  public serverGameConfig: IGameConfig = null;
  public urlParams: any;
  public mode: string = "dev";
  public replayToken: string = "";
  public gameName: string = "";
  public country: string;
  public defaultCurrencyFormat: IMoneyFieldConfig = null;
  public isDemo = true;
  public homeUrl: string;
  public hasRealityCheck: boolean = false;
  public forceRealityCheck: boolean = false;
  public isWinSkipped: boolean = false;
  public layout: number[] = [];

  public regulation: string;
  public jurisdiction: string;
  public inBonusFeature: boolean = false;
  public restoreVersion: number = 1;
  public restoreData: any = null;
  public _turboSpin: boolean = false;
  public _spaceTurboSpin: boolean = false;
  public spaceToSpin: boolean = true;
  public shouldPauseGame: boolean = false;
  public shouldStopAutoplay: boolean = false;
  public apiConfig: any;
  public timeScale: string;
  public debugBounds: boolean;
  protected spinDelay: number;
  shouldUpdateBalance: boolean = false;
  private showReelConfigurator: boolean;
  private app;
  private reelExtraSymbolsCount: number = -1;
  private reelExtraSymbolsMaxCount: number = 10;
  public mathVersion: string = "";
  public freeSpinSymbol: number;
  public partnerid: string;

  constructor() {
    super();
    this.mobile = /Mobi/.test(navigator.userAgent);
    this.android = /android/i.test(navigator.userAgent);
    this.iOS = /(iPad|iPhone|iPod)/i.test(navigator.userAgent) && /(iPad|iPhone|iPod)/i.test(navigator.platform);;
    this.isIpad = /(iPad)/i.test(navigator.userAgent);
    if (this.iOS) {
      this.iOSMajorVersion = this.getIOSversion()[0];
    }

    this.isMac = navigator.platform.toUpperCase().indexOf('MAC')>= 0;
  }

  onBeforeReelGroupCreated(app) {
    this.app = app;
    if (this.showReelConfigurator && this.reelExtraSymbolsCount > -1) {
      app.config.reelExtraSymbols = Math.max(1, Math.min(this.reelExtraSymbolsMaxCount, this.reelExtraSymbolsCount));
    }
  }

  onAfterReelGroupCreated(reelGroup) {
    if (this.showReelConfigurator) {
      ReelConfigurator.init(this.app, reelGroup, "reelExtraSymbolsCount", this.reelExtraSymbolsMaxCount, this.isMobile(true));
    }
  }

  public parseUrlParams(urlParams, gameConfig) {
    this.urlParams = urlParams;
    this.lang = urlParams.language || urlParams.lang || urlParams.LanguageCode || "en";
    this.version = urlParams.version || gameConfig.version;
    this.lobbyURL = urlParams.lobbyurl;
    this.regulation = urlParams.regulation || Regulations.Unregulated;
    this.jurisdiction = urlParams.jurisdiction || ' ';

    if (urlParams.clock != undefined) {
      // clock url param has bigger priority than game config hideClock
      this.clock = urlParams.clock !== "false";
    } else {
      // By default we show the clock
      this.clock = true;
      if (gameConfig.hideClock != undefined) {
        this.clock = gameConfig.hideClock != true;
      }
    }

    this.authToken =
      urlParams.authToken || gameConfig.authToken;
    this.casino =
      urlParams.casino || gameConfig.casino;
    this.operatorToken = urlParams.operatorToken || gameConfig.operatorToken;
    this.playerToken = urlParams.playerToken || gameConfig.playerToken;
    this.sessionID = urlParams.sessionID || gameConfig.sessionID;
    this.isDemo = urlParams.isDemo === "true";
    this.homeUrl = urlParams.homeUrl || urlParams.homeurl;
    this.partnerid = urlParams.partnerid || urlParams.partnerId;

    var debug = process.env.NODE_ENV !== 'production';
    if (debug) {
      this.timeScale = urlParams.timeScale || urlParams.timescale;
      if (urlParams.showConsole === "true" || urlParams.showconsole === "true") {
        ConsoleUtil.enableConsoleOverlay();
      }

      if (urlParams.debugBounds === "true") {
        this.debugBounds = true;
      }

      if (urlParams.debugBounds === "true") {
        this.debugBounds = true;
      }
      
      this.showReelConfigurator = urlParams.showReelConfigurator === "true";
      if (this.showReelConfigurator && urlParams.reelExtraSymbolsCount) {
        this.reelExtraSymbolsCount = parseInt(urlParams.reelExtraSymbolsCount);
      }

      this.currency = urlParams.currency;
      this.coinsAmount = urlParams.coinsAmount;
    }
    
    this.parseJurisdictionVars(gameConfig);
  }
  parseJurisdictionVars(gameConfig) {
    if (gameConfig.disableAutoplayJurisdictions && gameConfig.disableAutoplayJurisdictions.indexOf(this.jurisdiction.toUpperCase()) != -1) {
      gameConfig.disableAutoplay = true;

    }

    if (ConfigManager.hasConfig(ConfigurationTypes.GAME)) {
      Configs.game.buyFeature?.changeByJurisdiction(this.jurisdiction);
    }

    this._gameConfig = { ...gameConfig };
  }

  public get gameConfig() {
    return this._gameConfig;
  }

  public set gameConfig(cfg: any) {
    ConsoleUtil.warn("REMOVE GAME CONFIG SETTING FROM INDEX.TS");
  }


  public incrementRestoreVersion() {
    this.restoreVersion++;
  }

  public forceEnableRules(): boolean {
    return this.gameConfig.FORCE_RULES_JURISDICTIONS.indexOf(this.jurisdiction.toUpperCase()) !== -1;
  }

  public isMobile(really: boolean = false): boolean {
    if (!really && this.gameConfig.forceMobile) {
      return true;
    }

    if (!really && this.urlParams.channel == "mobile") {
      return true;
    }
    
    return this.mobile || this.android || this.iOS;
  }

  public setSpinDelay(delay: number) {
    if (delay && delay > 0) {
      this.spinDelay = delay;
    } else {
      this.spinDelay = undefined;
    }
  }

  public getSpinDelay(): number {
    return this.spinDelay || 0;
  }

  //TODO this functions are moved to UIController
  // public addTurboListener() {
  // }

  // public removeTurboListener() {
  // }

  get turboSpin() {
    return this._turboSpin || this._spaceTurboSpin;
  }

  public toggleTurboSpin() {
    if(this.canSkipSpin()) {
      this._turboSpin = !this._turboSpin;
    }
  }

  public toggleSpaceTurboSpin() {
    if(this.canSkipSpin()) {
      this._spaceTurboSpin = !this._spaceTurboSpin;
    }
  }

  public canSkipSpin(): boolean {
    if (this.gameConfig.NO_SKIP_JURISDICTION && this.gameConfig.NO_SKIP_JURISDICTION.length) {
      return this.gameConfig.NO_SKIP_JURISDICTION.indexOf(this.jurisdiction.toUpperCase()) == -1;
    } else {
      return true;
    }
  }

  public alowSpaceToSkipSpin():boolean {
    if(this.canSkipSpin())  {
      return this.spaceToSpin;
    }
    return false;
  }

  public toggleSpaceToSpin() {
    if (this.spaceToSpin) {
      this.spaceToSpin = false;
    } else {
      this.spaceToSpin = true;
    }
    if(this.isMobile()) {
      this.spaceToSpin = false;
    }
  }

  public getLobbyURL(): string {
    return this.lobbyURL || null;
  }

  private getIOSversion() {
    if (/iP(hone|od|ad)/.test(navigator.platform)) {
      var v = (navigator.appVersion).match(/OS (\d+)_(\d+)_?(\d+)?/);
      return [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3] || "0", 10)];
    }
  }
}

export enum Regulations {
  Unregulated = "unregulated",
  UK = "uk",
}

export default new GameModel();
